import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, apiErrorMessage, callFunction } from "../api/client";
import type { Event } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { PAGE_SIZE, useListParams } from "../lib/useListParams";
import { ListShell, SortHeader } from "../components/ListShell";
import { useCsvColumns } from "../lib/columnChoice";
import { downloadNarrowed } from "../lib/csvDownload";
import { orAbsent, relativeDate, shortDate } from "../lib/format";
import { Spinner } from "../components/Spinner";

/* Alerts: every failure, alert and notice the engine has raised, with its
 * status, from api.event (engine migrations 154 and 155).
 *
 * James, 2026-10-05: emails were the worst way to track alerts. The engine
 * now records every failed unit when it fails, every alert email, and the
 * watchdog's all-clear, in the lake. This is that log for an admin, and the
 * place to say a problem is being worked on, resolved, or needs nothing.
 *
 * One problem is one row. A unit that fails on every run keeps one open row
 * with a count, until somebody closes it. A closed status needs a note, and
 * the engine refuses one without, so the form asks for it. */
const STATUS_WORDS: Record<string, { label: string; means: string }> = {
  new:         { label: "New",         means: "Recorded. Not yet looked at." },
  working:     { label: "Working",     means: "Somebody is looking into it." },
  resolved:    { label: "Resolved",    means: "Dealt with. The note says what was done." },
  disregarded: { label: "Disregarded", means: "Needs no action. The note says why." },
};

const SEVERITY_WORDS: Record<string, string> = { error: "Error", warning: "Warning", info: "Notice" };

const CSV_DEFAULT = ["severity", "status", "subject", "summary", "occurrences", "first_seen_at", "last_seen_at", "status_by", "status_note"] as const;

export function Alerts({ session }: { session: SignedIn }) {
  /* Open problems first by default: the filter starts on "open", which is
     new and working, worst first. */
  const params = useListParams("last_seen_at", ["status", "severity"] as const, false, "event");
  const csvColumns = useCsvColumns("alerts", "event", CSV_DEFAULT);
  const queryClient = useQueryClient();
  const status = params.filters["status"] ?? "open";

  const narrowed = (columns?: string, opts?: { count?: "exact" }) => {
    let q = apiClient(session.idToken).from("event").select(columns ?? "*", opts);
    if (status === "open") q = q.in("status", ["new", "working"]);
    else if (status) q = q.eq("status", status);
    if (params.filters["severity"]) q = q.eq("severity", params.filters["severity"]);
    // The characters or() reads as its own syntax stand as the wildcard, so
    // a comma in the search still matches the comma in the text.
    const s = params.search.trim().replace(/[(),*"\\]/g, "*");
    if (s) q = q.or(`subject.ilike.*${s}*,summary.ilike.*${s}*`);
    return q;
  };

  const query = useQuery({
    queryKey: ["alerts", params.page, params.sort, params.ascending, params.filters, params.search],
    refetchInterval: 60_000,
    queryFn: async () => {
      const from = (params.page - 1) * PAGE_SIZE;
      const { data, error, count } = await narrowed(
        "id,first_seen_at,last_seen_at,occurrences,source,kind,severity,subject,summary,detail,status,status_at,status_by,status_note,closed_at",
        { count: "exact" })
        .order(params.sort, { ascending: params.ascending, nullsFirst: false })
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw new Error(apiErrorMessage(error));
      return { rows: (data ?? []) as Event[], total: count ?? null };
    },
  });

  const rows = query.data?.rows ?? [];

  return (
    <ListShell
      title="Alerts"
      view="event"
      searchLabel="Subject or summary"
      params={params}
      csvColumns={csvColumns}
      total={query.data?.total ?? null}
      rowCount={rows.length}
      isLoading={query.isLoading}
      isFetching={query.isFetching}
      isError={query.isError}
      error={query.error as Error | null}
      dataUpdatedAt={query.dataUpdatedAt}
      onRefresh={() => queryClient.invalidateQueries({ queryKey: ["alerts"] })}
      onRetry={() => query.refetch()}
      onDownload={() =>
        downloadNarrowed({
          name: "alerts",
          columns: csvColumns.chosen,
          total: query.data?.total ?? null,
          fetchAll: async (ceiling) => {
            const { data, error } = await narrowed(csvColumns.chosen.join(","))
              .order(params.sort, { ascending: params.ascending, nullsFirst: false })
              .range(0, ceiling - 1);
            if (error) throw new Error(apiErrorMessage(error));
            return (data ?? []) as unknown as Event[];
          },
        })
      }
      emptyEverything={status === "open" ? "No open alerts. Every failure and alert has been dealt with or set aside." : "No alerts match."}
      filters={
        <>
          <label htmlFor="f-status">Status</label>
          <select id="f-status" value={status}
                  onChange={(e) => params.set({ filters: { ...params.filters, status: e.target.value }, page: 1 })}>
            <option value="open">open (new and working)</option>
            {Object.entries(STATUS_WORDS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            <option value="all">everything</option>
          </select>
          <label htmlFor="f-severity">Severity</label>
          <select id="f-severity" value={params.filters["severity"] ?? ""}
                  onChange={(e) => params.set({ filters: { ...params.filters, severity: e.target.value }, page: 1 })}>
            <option value="">any</option>
            {Object.entries(SEVERITY_WORDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </>
      }
    >
      <table>
        <thead>
          <tr>
            <th>Severity</th>
            <th>Status</th>
            <th>What</th>
            <SortHeader column="last_seen_at" label="Last seen" params={params} />
            <th className="num">Times</th>
            <th>Set by</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => <AlertRow key={e.id} token={session.idToken} e={e} />)}
        </tbody>
      </table>
    </ListShell>
  );
}

function AlertRow({ token, e }: { token: string; e: Event }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState<string>(e.status === "new" ? "working" : e.status ?? "working");
  const [note, setNote] = useState(e.status_note ?? "");
  const [said, setSaid] = useState<string | null>(null);
  const set = useMutation({
    mutationFn: () => callFunction(token, "set_event_status", { p_id: e.id, p_status: to, p_note: note.trim() || null }),
    onSuccess: () => { setSaid(null); setOpen(false); queryClient.invalidateQueries({ queryKey: ["alerts"] }); },
    onError: (err: Error) => setSaid(err.message),
  });
  const needsNote = to === "resolved" || to === "disregarded";
  return (
    <>
      <tr className="clickable" onClick={() => setOpen((o) => !o)}>
        <td className={e.severity === "error" ? "refusal" : e.severity === "warning" ? "" : "muted"}>
          {SEVERITY_WORDS[e.severity ?? ""] ?? e.severity}
        </td>
        <td title={STATUS_WORDS[e.status ?? ""]?.means}>{STATUS_WORDS[e.status ?? ""]?.label ?? e.status}</td>
        <td>
          <div>{orAbsent(e.subject)}</div>
          <div className="muted">{e.summary}</div>
        </td>
        <td title={typeof e.last_seen_at === "string" ? e.last_seen_at : undefined}>{relativeDate(e.last_seen_at)}</td>
        <td className="num">{e.occurrences ?? 1}</td>
        <td className="muted">
          {e.status_by ? <>{e.status_by}{e.status_at ? <>, {shortDate(e.status_at)}</> : null}</> : orAbsent(null)}
          {e.status_note ? <div>{e.status_note}</div> : null}
        </td>
      </tr>
      {open ? (
        <tr>
          <td colSpan={6}>
            <div className="fields">
              <label>
                <div className="field-label">Set status</div>
                <select value={to} onChange={(ev) => setTo(ev.target.value)}>
                  {Object.entries(STATUS_WORDS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <div className="muted" style={{ fontSize: 12 }}>{STATUS_WORDS[to]?.means}</div>
              </label>
              <label>
                <div className="field-label">{needsNote ? "What was done, or why it needs no action" : "Note"}</div>
                <input value={note} onChange={(ev) => setNote(ev.target.value)} style={{ width: "100%" }} />
              </label>
            </div>
            <div className="bar">
              <button onClick={() => set.mutate()} disabled={set.isPending || (needsNote && !note.trim())}>
                {set.isPending ? <><Spinner />Saving</> : "Save"}
              </button>
              <button onClick={() => setOpen(false)}>Close</button>
              {said ? <span className="refusal" role="alert">{said}</span> : null}
            </div>
            {e.detail ? (
              <details>
                <summary>What the engine recorded</summary>
                <pre className="detail">{e.detail}</pre>
              </details>
            ) : null}
            <p className="muted" style={{ fontSize: 12 }}>
              First seen {shortDate(e.first_seen_at)}, from {e.source === "unit" ? "the failure hook" : e.source === "alert_topic" ? "the alert emails"
                : e.source === "screen" ? "EVIE Sales" : e.source === "engine" ? "the engine" : `the ${e.source}`}.
            </p>
          </td>
        </tr>
      ) : null}
    </>
  );
}
